import { useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import Input from "../../form/input/InputField";
import Select from "../../../components/form/Select";
import MultiSelectCheckbox from '../../form/MultiSelectCheckbox';
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import { useNavigate } from "react-router";
import axios from "axios";
import { authService } from "../../../services/authService";
import Radio from "../../../components/form/input/Radio";

interface MemberkitFormData {
    active: boolean;
    name: string;
    apiKey: string;
    offerList: string[];
    global: boolean;
    type: 'global' | 'specific';
    memberkitDelivery?: string;
}

interface NewMemberkitFormProps {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    companyId?: string;
}

export default function NewMemberkitForm({ onSuccess, onError, companyId: companyIdProp }: NewMemberkitFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<MemberkitFormData>({
        name: "",
        apiKey: "",
        offerList: [],
        global: false,
        active: true,
        type: 'specific',
        memberkitDelivery: "",
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<Array<{ value: string; label: string }>>([]);
    const [deliveries, setDeliveries] = useState<Array<{ value: string; label: string }>>([]);
    const [deliveryType, setDeliveryType] = useState<'ilimitada' | 'assinatura' | 'curso'>('ilimitada');
    const [memberships, setMemberships] = useState<Array<{ value: string; label: string }>>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
    const [apiKeyValidated, setApiKeyValidated] = useState(false);

    const companyId = companyIdProp || authService.getCompanyId?.();

    // Valida a chave API da Memberkit
    const validateMemberkitApiKey = async (apiKey: string) => {
        setLoading(true);
        setApiError("");
        setApiSuccess("");
        setDeliveries([]);
        try {
            const resp = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/membership/validate`,
                { provider: "memberkit", apiKey },
                { headers: { 'Authorization': `Bearer ${authService.getToken()}` } }
            );

            if (resp.data && resp.data.success) {
                setApiKeyValidated(true);
                setApiSuccess("Chave validada com sucesso!");
                setApiError("");
                // Cursos (entregas)
                const deliveriesArray = resp.data.deliveries || [];
                setDeliveries(deliveriesArray.map((d: any) => ({
                    value: d.id,
                    label: d.name
                })));
                // Assinaturas (memberships)
                const membershipsArray = resp.data.membership || [];
                setMemberships(membershipsArray.map((m: any) => ({
                    value: m.id,
                    label: m.name
                })));
                await fetchOffers();
            } else {
                setApiError(resp.data?.error || "Chave inválida.");
                setApiSuccess("");
                setApiKeyValidated(false);
                setDeliveries([]);
                setMemberships([]);
            }
        } catch (error: any) {
            setApiError("Chave inválida ou erro na validação.");
            setApiSuccess("");
            setApiKeyValidated(false);
            setDeliveries([]);
            setMemberships([]);
        } finally {
            setLoading(false);
        }
    };

    // Busca as ofertas da Empresa (após chave validada)
    const fetchOffers = async () => {
        setLoadingOffers(true);
        setOffers([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/company-offers/${companyId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.success && Array.isArray(resp.data.data)) {
                const mappedOffers = resp.data.data.map((offer: any) => ({
                    value: offer.id,
                    label: offer.name
                }));
                setOffers(mappedOffers);
            }
        } catch (error) {
            setOffers([]);
            setApiError("Erro ao buscar ofertas.");
        } finally {
            setLoadingOffers(false);
        }
    };

    // Sempre que alterar nome ou chave, reseta os estados de validação/erro/sucesso
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setErrors((prev) => ({ ...prev, [id]: "" }));
        setApiError("");
        setApiSuccess("");
        setApiKeyValidated(false);
        setDeliveries([]);
        setOffers([]);
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [id]: checked }));
    };

    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');
        setErrors((prev) => ({ ...prev, offerList: "" }));
        setApiError("");
        setApiSuccess("");
        if (isAllSelected || deliveryType === 'ilimitada') {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormData((prev) => ({
                ...prev,
                global: true,
                offerList: [],
                type: 'global',
            }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormData((prev) => ({
                ...prev,
                global: false,
                offerList: values,
                type: 'specific',
            }));
        }
    };


    const handleDeliveryChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            memberkitDelivery: value,
        }));
        setErrors((prev) => ({ ...prev, memberkitDelivery: "" }));
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.name) newErrors.name = "Nome da integração é obrigatório";
        if (!formData.apiKey) newErrors.apiKey = "Chave API é obrigatória";
        // Só exige oferta se NÃO for global e NÃO for ilimitada
        if (
            apiKeyValidated &&
            !formData.global &&
            !formData.offerList.length &&
            deliveryType !== 'ilimitada'
        ) {
            newErrors.offerList = "Selecione ao menos uma oferta";
        }
        if (!formData.memberkitDelivery && deliveryType !== 'ilimitada')
            newErrors.memberkitDelivery = "Selecione uma entrega";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setApiError("");
        setApiSuccess("");

        let type = '';
        let externalDeliveryId = null;

        if (deliveryType === 'ilimitada') {
            type = 'unlimited';
        } else if (deliveryType === 'assinatura') {
            type = 'membership_level_id';
            externalDeliveryId = formData.memberkitDelivery || null;
        } else if (deliveryType === 'curso') {
            type = 'classroom_ids';
            externalDeliveryId = formData.memberkitDelivery || null;
        }

        try {
            const integrationData: any = {
                name: formData.name,
                provider: "memberkit",
                apiKey: formData.apiKey,
                apiUrl: 'https://memberkit.com.br/api/v1/',
                companyId,
                type, // <-- "unlimited", "membership_level_id" ou "classroom_ids"
                externalDeliveryId: String(externalDeliveryId || ''),
                offersList: formData.global ? [] : formData.offerList,
                global: formData.global,
                active: formData.active,
            };

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/membership`,
                integrationData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );
            if (response.data && !response.data.error) {
                setApiSuccess("Integração criada com sucesso!");
                if (onSuccess) onSuccess(response.data);
                setTimeout(() => navigate('/apps/memberkit'), 1500);
            } else {
                setApiError(response.data.error || "Erro ao criar integração");
                if (onError) onError(response.data.error);
            }
        } catch (error: any) {
            setApiError(error.response?.data?.error || error.message || "Erro ao criar integração");
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };


    const handleGoBack = () => navigate(`/apps/memberkit`);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Criar nova integração Memberkit">
                    {/* Só mostra mensagem de erro se não tiver sucesso */}
                    {apiError && !apiSuccess && (
                        <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                            {apiError}
                        </div>
                    )}
                    {apiSuccess && !apiError && (
                        <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                            {apiSuccess}
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); if (apiKeyValidated && !apiError) handleSubmit(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar integração"
                                defaultChecked={true}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome da integração *</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                onChange={handleInputChange}
                                value={formData.name}
                                error={!!errors.name}
                                hint={errors.name}
                                disabled={apiKeyValidated}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="apiKey">Chave API *</Label>
                            <Input
                                type="text"
                                id="apiKey"
                                placeholder="Sua chave API"
                                onChange={handleInputChange}
                                value={formData.apiKey}
                                error={!!errors.apiKey}
                                hint={errors.apiKey}
                                disabled={apiKeyValidated}
                            />
                        </div>

                        {/* Só mostra o botão de validação enquanto não estiver validado e não houver sucesso */}
                        {!apiKeyValidated && !apiSuccess && (
                            <div className="mt-6 flex justify-end gap-4.5">
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => validateMemberkitApiKey(formData.apiKey)}
                                    disabled={loading || !formData.apiKey || !formData.name}
                                >
                                    {loading ? "Validando..." : "Validar chave API"}
                                </Button>
                            </div>
                        )}

                        {/* Segunda parte só se a chave foi validada E não tiver erro */}
                        {apiKeyValidated && !apiError && (
                            <>
                                <div className="mt-5">
                                    <Label>Ofertas *</Label>
                                    {loadingOffers ? (
                                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                            Carregando ofertas...
                                        </div>
                                    ) : (
                                        <div className="mt-2">
                                            <MultiSelectCheckbox
                                                options={[
                                                    {
                                                        id: 'all',
                                                        label: 'Qualquer oferta',
                                                        disabled: false,
                                                    },
                                                    ...offers.map((offer) => ({
                                                        id: offer.value,
                                                        label: `${offer.label}`,
                                                        disabled: false,
                                                        meta: offer,
                                                    })),
                                                ]}
                                                selectedValues={selectAllOffers ? ['all'] : selectedOffers}
                                                onChange={handleOffersChange}
                                                placeholder="Selecione as ofertas"
                                                error={!!errors.offerList}
                                                hint={errors.offerList}
                                                searchPlaceholder="Pesquisar ofertas"
                                                showSelectAll={false}
                                            />
                                            {selectAllOffers && (
                                                <p className="mt-1 text-xs text-blue-500">
                                                    Esta integração será válida para todas as ofertas, incluindo futuras.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <Label>Entrega *</Label>
                                    <div className="flex flex-col mt-3 gap-4">
                                        <Radio
                                            id="deliveryType-ilimitada"
                                            name="deliveryType"
                                            value="ilimitada"
                                            checked={deliveryType === 'ilimitada'}
                                            label="Ilimitada (todos os cursos)"
                                            onChange={(value) => {
                                                setDeliveryType(value as 'ilimitada' | 'assinatura' | 'curso');
                                                handleDeliveryChange('ilimitada');
                                            }}
                                        />

                                        <Radio
                                            id="deliveryType-assinatura"
                                            name="deliveryType"
                                            value="assinatura"
                                            checked={deliveryType === 'assinatura'}
                                            label="Adicionar a assinatura"
                                            onChange={(value) => {
                                                setDeliveryType(value as 'ilimitada' | 'assinatura' | 'curso');
                                                handleDeliveryChange('');
                                            }}
                                        />
                                        {deliveryType === 'assinatura' && (
                                            <div>
                                                {memberships.length === 0 ? (
                                                    <div className="text-sm text-gray-400 py-2 px-3 border rounded bg-gray-50">
                                                        Nenhuma assinatura encontrada
                                                    </div>
                                                ) : (
                                                    <Select
                                                        options={memberships}
                                                        placeholder="Selecione uma assinatura"
                                                        onChange={handleDeliveryChange}
                                                        value={formData.memberkitDelivery}
                                                        className={errors.memberkitDelivery ? "border-red-500" : ""}
                                                    />
                                                )}
                                            </div>
                                        )}


                                        <Radio
                                            id="deliveryType-curso"
                                            name="deliveryType"
                                            value="curso"
                                            checked={deliveryType === 'curso'}
                                            label="Adicionar ao curso"
                                            onChange={(value) => {
                                                setDeliveryType(value as 'ilimitada' | 'assinatura' | 'curso');
                                                handleDeliveryChange('');
                                            }}
                                        />
                                        {deliveryType === 'curso' && (
                                            <div>
                                                {deliveries.length === 0 ? (
                                                    <div className="text-sm text-gray-400 py-2 px-3 border rounded bg-gray-50">
                                                        Nenhum resultado encontrado
                                                    </div>
                                                ) : (
                                                    <Select
                                                        options={deliveries}
                                                        placeholder="Selecione um curso"
                                                        onChange={handleDeliveryChange}
                                                        value={formData.memberkitDelivery}
                                                        className={errors.memberkitDelivery ? "border-red-500" : ""}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {errors.memberkitDelivery && <p className="mt-1 text-sm text-red-500">{errors.memberkitDelivery}</p>}
                                </div>

                                <div className="mt-6 flex justify-end gap-4.5">
                                    <Button
                                        size="md"
                                        variant="outline"
                                        onClick={handleGoBack}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        size="md"
                                        variant="primary"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? "Salvando..." : "Criar integração"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}
